/**
 * The inventory subtree of a save record, checked for shape and nothing else.
 *
 * There are two questions to ask of an inventory off disk, and they belong to different modules.
 * "Is this the right shape" is a question about JSON, and it is answered here. "Does this name
 * items and slots this build has, at counts the defs allow" is a question about the game, and
 * `migrateInventory` answers it one directory over in `sim/inventory`.
 *
 * Splitting them is what lets `sim` stay unaware that a disk exists. `StoredInventory` is already
 * the inventory's own word for "as it comes back", so this hands it exactly that and nothing wider.
 */

import { asArray, asNullableNumber, asNumber, asRecord, asString } from '@/save/recordFields';
import { saved } from '@/save/saveResult';
import type { SaveResult } from '@/save/saveResult';
import type {
  StoredContainer,
  StoredInventory,
  StoredStack,
} from '@/sim/inventory/inventorySchema';
import type { StoredRolledWeapon } from '@/sim/inventory/storedWeaponRoll';

export function asStoredInventory(value: unknown, path: string): SaveResult<StoredInventory> {
  const record = asRecord(value, path);
  if (!record.ok) return record;

  const version = asNumber(record.value.version, `${path}.version`);
  if (!version.ok) return version;

  const carried = asStoredContainer(record.value.carried, `${path}.carried`);
  if (!carried.ok) return carried;

  const stash = asStoredContainer(record.value.stash, `${path}.stash`);
  if (!stash.ok) return stash;

  const equipment = asStoredEquipment(record.value.equipment, `${path}.equipment`);
  if (!equipment.ok) return equipment;

  const nextInstanceId = asNumber(record.value.nextInstanceId, `${path}.nextInstanceId`);
  if (!nextInstanceId.ok) return nextInstanceId;

  return saved({
    version: version.value,
    carried: carried.value,
    stash: stash.value,
    equipment: equipment.value,
    nextInstanceId: nextInstanceId.value,
  });
}

function asStoredContainer(value: unknown, path: string): SaveResult<StoredContainer> {
  const record = asRecord(value, path);
  if (!record.ok) return record;

  const capacityKg = asNumber(record.value.capacityKg, `${path}.capacityKg`);
  if (!capacityKg.ok) return capacityKg;

  const entries = asArray(record.value.stacks, `${path}.stacks`);
  if (!entries.ok) return entries;

  const stacks: StoredStack[] = [];
  for (const [index, entry] of entries.value.entries()) {
    const stack = asStoredStack(entry, `${path}.stacks[${String(index)}]`);
    if (!stack.ok) return stack;
    stacks.push(stack.value);
  }

  return saved({ capacityKg: capacityKg.value, stacks });
}

/**
 * Slot names are left alone here. An unknown key is a slot this build dropped, and saying so by
 * name is `migrateInventory`'s job: it has the registry, and it already has the failure for it.
 */
function asStoredEquipment(
  value: unknown,
  path: string,
): SaveResult<Readonly<Record<string, StoredStack | null>>> {
  const record = asRecord(value, path);
  if (!record.ok) return record;

  const equipment: Record<string, StoredStack | null> = {};
  for (const [slot, entry] of Object.entries(record.value)) {
    if (entry === null) {
      equipment[slot] = null;
      continue;
    }
    const stack = asStoredStack(entry, `${path}.${slot}`);
    if (!stack.ok) return stack;
    equipment[slot] = stack.value;
  }

  return saved(equipment);
}

function asStoredStack(value: unknown, path: string): SaveResult<StoredStack> {
  const record = asRecord(value, path);
  if (!record.ok) return record;

  const defId = asString(record.value.defId, `${path}.defId`);
  if (!defId.ok) return defId;

  const count = asNumber(record.value.count, `${path}.count`);
  if (!count.ok) return count;

  const instanceId = asNullableNumber(record.value.instanceId, `${path}.instanceId`);
  if (!instanceId.ok) return instanceId;

  const rolled = asStoredRoll(record.value.rolled, `${path}.rolled`);
  if (!rolled.ok) return rolled;

  return saved({
    defId: defId.value,
    count: count.value,
    instanceId: instanceId.value,
    rolled: rolled.value,
  });
}

/**
 * A roll's values are shape-checked and left as written. `readRolledWeapon` clamps the magnitudes
 * and re-derives the name, which is a game rule and not a JSON one.
 */
function asStoredRoll(value: unknown, path: string): SaveResult<StoredRolledWeapon | null> {
  if (value === null) return saved(null);

  const record = asRecord(value, path);
  if (!record.ok) return record;

  const weaponId = asString(record.value.weaponId, `${path}.weaponId`);
  if (!weaponId.ok) return weaponId;

  const rarity = asString(record.value.rarity, `${path}.rarity`);
  if (!rarity.ok) return rarity;

  const prefixId = asString(record.value.prefixId, `${path}.prefixId`);
  if (!prefixId.ok) return prefixId;

  const suffixId = asString(record.value.suffixId, `${path}.suffixId`);
  if (!suffixId.ok) return suffixId;

  const name = asString(record.value.name, `${path}.name`);
  if (!name.ok) return name;

  const prefixMagnitude = asNumber(record.value.prefixMagnitude, `${path}.prefixMagnitude`);
  if (!prefixMagnitude.ok) return prefixMagnitude;

  const suffixMagnitude = asNumber(record.value.suffixMagnitude, `${path}.suffixMagnitude`);
  if (!suffixMagnitude.ok) return suffixMagnitude;

  return saved({
    weaponId: weaponId.value,
    rarity: rarity.value,
    prefixId: prefixId.value,
    suffixId: suffixId.value,
    prefixMagnitude: prefixMagnitude.value,
    suffixMagnitude: suffixMagnitude.value,
    name: name.value,
  });
}
